import { isClosedStatus } from '@bbbk/schema';
import type { Booking, BookingDog } from '../bookings/types';
import { isoDate, messageDateLabel } from '../../lib/dates';

// The "Dogs Leaving" staff message — pick a day, get the dogs collected that
// day as plain copy-paste text for the staff WhatsApp. Pure + side-effect free
// so it unit-tests without a DOM; the view owns the fetch and the clipboard.

// messageDateLabel ("Monday 29 June") moved to lib/dates — the On-duty card's
// group message reads it too, and today/ cannot import messages/ (messages/
// already imports today/, so the reverse would invert the dependency).
export { messageDateLabel };

export interface LeavingDog {
  name: string;
  breed: string | null;
}

// A booking is a departure on `dateIso` iff its check-out IS that day and it is
// a real (non-cancelled) stay — the same rule computeStats uses, so
// the message can never disagree with the calendar's "leaving today". Every dog
// on a qualifying booking becomes its own line; the result is sorted by name
// (then breed) for a stable, readable list regardless of fetch order.
export function dogsLeavingOn(
  bookings: Pick<Booking, 'checkOut' | 'status' | 'dogs'>[],
  dateIso: string,
): LeavingDog[] {
  const dogs: LeavingDog[] = [];
  for (const b of bookings) {
    if (isClosedStatus(b.status)) continue;
    if (isoDate(b.checkOut) !== dateIso) continue;
    for (const d of b.dogs as BookingDog[]) {
      dogs.push({ name: d.name, breed: d.breed ?? null });
    }
  }
  return dogs.sort(
    (a, b) => a.name.localeCompare(b.name) || (a.breed ?? '').localeCompare(b.breed ?? ''),
  );
}

// "Name (Breed)" — or just "Name" when no breed is on file (legacy/import dogs
// carry a null breed; staff still need the name).
function dogLine(d: LeavingDog): string {
  const breed = (d.breed ?? '').trim();
  return breed ? `• ${d.name} (${breed})` : `• ${d.name}`;
}

// The full WhatsApp message. Title + date, then a line per dog, then the
// brushing sign-off. An empty day says so plainly and drops the sign-off —
// nothing to prep, no instruction. Plain text, no emoji (Freddi 2026-06-29).
export function buildLeavingMessage(dateIso: string, dogs: LeavingDog[]): string {
  const header = `Dogs Leaving on ${messageDateLabel(dateIso)}`;
  if (dogs.length === 0) {
    return `${header}\n\nNo dogs leaving today.`;
  }
  const lines = dogs.map(dogLine).join('\n');
  return `${header}\n\n${lines}\n\nPlease check dogs are clean and brushed.`;
}
